// ============================================
// Patient Journey Engine Dashboard
// Main orchestrator with tab navigation
// NO framer-motion — matches FinancialDashboard pattern
// ============================================

import { useState, useEffect } from 'react';
import { Activity } from 'lucide-react';
import { ArchetypeLibrary } from './ArchetypeLibrary';
import { PatientBuilder } from './PatientBuilder';
import { PanelGeneratorView } from './PanelGeneratorView';
import { ContextPanel } from './ContextPanel';
import { JourneyTimeline } from './JourneyTimeline';
import { TwinRunner } from './TwinRunner';
import { ManualRunner } from './ManualRunner';
import { LoadSimPanel } from './LoadSimPanel';
import { TestResults } from './TestResults';
import { NurseTuning } from './NurseTuning';
import { ClinicalCalibration } from './ClinicalCalibration';
import { LabCalibration } from './LabCalibration';
import { JourneyStepConfig } from './JourneyStepConfig';
import { getTestRuns } from '../../../services/firestore/pjeService';
import { calculateReadinessScore } from '../../../services/patientJourney/journeyEngine';

type TabId = 'archetypes' | 'builder' | 'panel' | 'context' | 'journey' | 'twin' | 'manual' | 'tuning' | 'calibration' | 'lab-calibration' | 'loadsim' | 'results' | 'config';

const TABS: { id: TabId; label: string }[] = [
  { id: 'twin', label: 'Synthetic Army' },
  { id: 'manual', label: 'Manual Test' },
  { id: 'tuning', label: 'Nurse Tuning' },
  { id: 'calibration', label: 'Doctor Training' },
  { id: 'lab-calibration', label: 'Lab Calibration' },
  { id: 'archetypes', label: 'Archetypes' },
  { id: 'builder', label: 'Patient Builder' },
  { id: 'panel', label: 'Panel Generator' },
  { id: 'context', label: 'Context' },
  { id: 'journey', label: 'Journey' },
  { id: 'loadsim', label: 'Load Sim' },
  { id: 'results', label: 'Results' },
  { id: 'config', label: 'Config' },
];

export function PatientJourneyDashboard() {
  const [activeTab, setActiveTab] = useState<TabId>('twin');
  const [readiness, setReadiness] = useState<number | null>(null);
  const [readinessLoading, setReadinessLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadReadiness() {
      try {
        const runs = await getTestRuns();
        if (cancelled) return;
        const score = calculateReadinessScore(runs);
        setReadiness(score);
      } catch {
        if (!cancelled) setReadiness(null);
      } finally {
        if (!cancelled) setReadinessLoading(false);
      }
    }

    loadReadiness();
    return () => { cancelled = true; };
  }, []);

  const readinessColor =
    readiness === null
      ? 'text-text-muted'
      : readiness >= 85
        ? 'text-green-400'
        : readiness >= 60
          ? 'text-amber-400'
          : 'text-red-400';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-3xl font-display text-gold">Patient Journey Engine</h2>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-bg-card border border-bg-tertiary">
            <Activity size={16} className="text-gold" />
            <span className="text-base text-text-secondary">Readiness:</span>
            <span className={`text-base font-semibold ${readinessColor}`}>
              {readinessLoading ? '...' : readiness !== null ? `${readiness}%` : '--%'}
            </span>
          </div>
        </div>
      </div>

      {/* Tab bar — matches FinancialDashboard pattern */}
      <div className="flex gap-1 border-b border-bg-tertiary mb-6 pb-1 overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-base font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'text-gold border-b-2 border-gold'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'archetypes' && <ArchetypeLibrary />}
      {activeTab === 'builder' && <PatientBuilder />}
      {activeTab === 'panel' && <PanelGeneratorView />}
      {activeTab === 'context' && <ContextPanel />}
      {activeTab === 'journey' && <JourneyTimeline />}
      {activeTab === 'twin' && <TwinRunner />}
      {activeTab === 'manual' && <ManualRunner />}
      {activeTab === 'tuning' && <NurseTuning />}
      {activeTab === 'calibration' && <ClinicalCalibration />}
      {activeTab === 'lab-calibration' && <LabCalibration />}
      {activeTab === 'loadsim' && <LoadSimPanel />}
      {activeTab === 'results' && <TestResults />}
      {activeTab === 'config' && <JourneyStepConfig />}
    </div>
  );
}
